import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiKeyRecord, CreatedApiKeyRecord, SiteSummary } from '../../models/cms-models';
import { ApiKeysService } from '../../services/api-keys-service';
import { SiteService } from '../../services/site-service';

@Component({
  selector: 'app-api-keys-page',
  imports: [CommonModule, FormsModule],
  templateUrl: './api-keys-page.html',
  styleUrl: './api-keys-page.css',
})
export class ApiKeysPage implements OnInit {
  private siteService = inject(SiteService);
  private apiKeysService = inject(ApiKeysService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  apiKeys: ApiKeyRecord[] = [];
  selectedSiteId: string | null = null;
  draftName = '';
  createdKeyDialog: CreatedApiKeyRecord | null = null;
  copyMessage = '';
  loading = false;
  saving = false;
  error: string | null = null;
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadSites();
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      void this.syncSelectedSite(params.get('site'));
    });
  }

  get selectedSiteName(): string {
    return this.sites.find((site) => site.id === this.selectedSiteId)?.name ?? 'this site';
  }

  get totalSitesWithKeys(): number {
    return this.sites.filter((site) => site.publicApiKeyCount > 0).length;
  }

  changeSite(siteId: string): void {
    if (!siteId || siteId === this.selectedSiteId) {
      return;
    }

    this.closeCreatedKeyDialog();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { site: siteId },
      queryParamsHandling: 'merge',
    });
  }

  openSitesPage(): void {
    void this.router.navigate(['/sites']);
  }

  async createKey(): Promise<void> {
    if (!this.selectedSiteId || !this.draftName.trim()) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      const created = await this.apiKeysService.createApiKey(this.selectedSiteId, this.draftName.trim());
      this.apiKeys = [
        {
          id: created.id,
          name: created.name,
          siteId: created.siteId,
          keyPreview: created.keyPreview,
        },
        ...this.apiKeys,
      ];
      this.createdKeyDialog = created;
      this.copyMessage = '';
      this.draftName = '';
      this.sites = this.sites.map((site) =>
        site.id === this.selectedSiteId
          ? { ...site, publicApiKeyCount: site.publicApiKeyCount + 1 }
          : site,
      );
    } catch {
      this.error = 'Failed to create API key.';
    } finally {
      this.saving = false;
    }
  }

  async deleteKey(apiKey: ApiKeyRecord): Promise<void> {
    const confirmed = window.confirm(`Delete "${apiKey.name}" for ${this.selectedSiteName}? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      await this.apiKeysService.deleteApiKey(apiKey.id);
      this.apiKeys = this.apiKeys.filter((existing) => existing.id !== apiKey.id);
      if (this.createdKeyDialog?.id === apiKey.id) {
        this.closeCreatedKeyDialog();
      }
      this.sites = this.sites.map((site) =>
        site.id === this.selectedSiteId
          ? { ...site, publicApiKeyCount: Math.max(0, site.publicApiKeyCount - 1) }
          : site,
      );
      this.error = null;
    } catch {
      this.error = 'Failed to delete API key.';
    }
  }

  private async loadSites(): Promise<void> {
    try {
      this.sites = await this.siteService.getSites();
      if (!this.sites.length) {
        this.selectedSiteId = null;
        this.apiKeys = [];
        this.loadedSiteId = null;
      }
    } catch {
      this.error = 'Failed to load sites.';
    }
  }

  private async syncSelectedSite(routeSiteId: string | null): Promise<void> {
    if (!this.sites.length) {
      this.selectedSiteId = null;
      this.apiKeys = [];
      this.loadedSiteId = null;
      return;
    }

    const fallbackSiteId = this.sites[0].id;
    const targetSiteId =
      routeSiteId && this.sites.some((site) => site.id === routeSiteId) ? routeSiteId : fallbackSiteId;

    this.selectedSiteId = targetSiteId;

    if (routeSiteId !== targetSiteId) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { site: targetSiteId },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    await this.loadApiKeys(targetSiteId);
  }

  private async loadApiKeys(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;
    this.closeCreatedKeyDialog();

    try {
      this.apiKeys = await this.apiKeysService.getSiteApiKeys(siteId);
      this.loadedSiteId = siteId;
    } catch {
      this.error = 'Failed to load API keys for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  async copyCreatedKey(): Promise<void> {
    if (!this.createdKeyDialog) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.createdKeyDialog.key);
      this.copyMessage = 'API key copied to clipboard.';
    } catch {
      this.copyMessage = 'Unable to copy automatically. Copy the key manually.';
    }
  }

  closeCreatedKeyDialog(): void {
    this.createdKeyDialog = null;
    this.copyMessage = '';
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closeCreatedKeyDialog();
  }
}
